import React from 'react';
import moment from "moment";
import update from 'immutability-helper';
import fileDownload from 'js-file-download';
import SockJS from 'sockjs-client';
import Stomp from 'stompjs';

import {ValidationResult, validate, required, emailFormat, atLeastOneNotEmpty, forEach, generateStrategyResult, notEqual} from 'validation'
import {BaseDialog, Role} from 'dialogs/BaseDialog'
import Header from 'common/Header';
import LogoutTimer from './../LogoutTimer'
import theme from '../VpqClient.scss';
import {getMessage as msg} from 'MessageBundle'
import MainTab from './MainTab';
import InvoiceTab from './InvoiceTab';
import TextArea from 'common/TextArea';
import Input from 'common/Input';
import Popup from 'common/Popup';
import AttachmentList from './AttachmentList';
import CommentList from './CommentList';
import ImeiTab from './ImeiTab';
import DocumentTab from './DocumentTab';
import CountersigningPanel from './CountersigningPanel'
import FinanceTab from './FinanceTab';
import {TabPanel, TabView} from "common/TabView";
import ErrorBox from 'common/ErrorBox';
import {replaceJsxPlaceholders, replacePlaceholders} from 'common/Replace';
import {stringToFloat} from 'utils/Number';
import conf from 'configuration.json';
import ProgressBar from 'common/ProgressBar';
import loupePlusIcon from './loupePlus.png';
import loupeMinusIcon from './loupeMinus.png';

const SAVE_COMPLAINT = "SAVE";
const SUBMIT_COMPLAINT = "SUBMIT";
const RESUBMIT_COMPLAINT = "RESUBMIT";
const CLOSE_COMPLAINT = "CLOSE";

const COMPLAINT_ENDPOINTS = new Map([
   [SAVE_COMPLAINT, conf.rest.complaintSecureEndpoint],
   [SUBMIT_COMPLAINT, conf.rest.complaintSubmitEndpoint],
   [RESUBMIT_COMPLAINT, conf.rest.complaintResubmitEndpoint],
   [CLOSE_COMPLAINT, conf.rest.complaintCloseEndpoint]
]);

const COUNTERSIGN_AREA = "COUNTERSIGN_PANEL";

export default class EditComplaint extends BaseDialog {

    constructor(props) {
        super(props);

        const path = this.props.match.path; // current location path, e.g. /edit
        const pathname = this.props.location.pathname; // current location pathname, e.g. /edit/<complaintNumber>

        try {
            const params = atob(pathname.replace(path + "/", "")).split(";");
            this.complaintNumber = params[0];
            this.basket = params[1];
            this.requestedReadOnly = !!params[2];
        } catch (e) {
            window.location.href = '/';
        }
        this.state = {
            userComment: "",
            complaint: undefined,
            commentListPopupOpened: false,
            processing: false,
            newComments: [],
            countersigningResult: 'EMPTY',
            password: '',
            errorMessages: {},
            saved: {}
        }
    }

    getValidation(editor, documentTabValidationRequired, userCommentRequired) {
        const commonGroup = [
            {
                properties: ["state.complaint.email"],
                individualNames: ["email"],
                strategies: [
                    required(msg('editComplaint.validation.emailEmpty')),
                    emailFormat(msg('editComplaint.validation.emailFormat'))
                ]
            }
        ];

        if(userCommentRequired) {
            commonGroup.push(
                {
                    properties: ["state.userComment"],
                    individualNames: ["userComment"],
                    strategies: [
                        required(msg('editComplaint.validation.commentEmpty'))
                    ]
                })
        }

        if(editor.sales && !this.readOnly) {
            return {
                common: commonGroup,
                mainTab: [
                    {
                        properties: ["state.complaint.mainTab.hvo.vo"],
                        individualNames: ["hvoVo"],
                        strategies: [
                            required(msg('editComplaint.mainTab.validation.hvo.voRequired'))
                        ]
                    }, {
                        properties: ["state.complaint.mainTab.hvo.name1", "state.complaint.mainTab.hvo.name2"],
                        individualNames: ["hvoName"],
                        strategies: [
                            atLeastOneNotEmpty(msg('editComplaint.mainTab.validation.hvo.name'))
                        ]
                    }, {
                        properties: ["state.complaint.mainTab.type"],
                        individualNames: ["type"],
                        strategies: [
                            required(msg('editComplaint.mainTab.validation.typeEmpty'))
                        ]
                    }, {
                        properties: ["state.complaint.mainTab.reason"],
                        individualNames: ["reason"],
                        strategies: [
                            required(msg('editComplaint.mainTab.validation.reasonEmpty'))
                        ]
                    }, {
                        properties: ["state.complaint.mainTab.cause"],
                        individualNames: ["cause"],
                        strategies: [
                            required(msg('editComplaint.mainTab.validation.causeEmpty'))
                        ]
                    }, {
                        properties: ["state.complaint.mainTab.product"],
                        individualNames: ["product"],
                        strategies: [
                            required(msg('editComplaint.mainTab.validation.productEmpty'))
                        ]
                    },
                    {
                        properties: ["state.complaint.invoiceTab.approvedAmountNet"],
                        individualNames: ["approvedAmountNet"],
                        strategies: [
                            required(msg('editComplaint.mainTab.validation.approvedAmountNet'))
                        ]
                    }, {
                        properties: ["state.complaint.invoiceTab.approvedAmountTotal"],
                        individualNames: ["approvedAmountTotal"],
                        strategies: [
                            required(msg('editComplaint.mainTab.validation.approvedAmountTotal'))
                        ]
                    }
                ],
                documentTab: [
                    {
                        properties: ["state.complaint.documentTab.attachments"],
                        strategies: [
                            forEach([
                                {
                                    properties: ["type"],
                                    individualNames: ["documentType"],
                                    strategies: [
                                        required(msg("documentTab.fileTypeError"))
                                    ]
                                }
                            ])
                        ]
                    }
                ],
                documentTabOnSubmit: [
                    {
                        strategies: [
                            this.attachmentsExist,
                            this.allAttachmentsViewed
                        ]
                    }
                ],
                documentTabOnCountersign: [
                    {
                        properties: ["state.countersigningResult"],
                        individualNames: ["countersigningResult"],
                        strategies: [
                            notEqual('EMPTY', msg('editComplaint.documentTab.validation.emptyCountersigningResult'))
                        ]
                    }, {
                        properties: ["state.password"],
                        individualNames: ["password"],
                        strategies: [
                            required(msg('editComplaint.documentTab.validation.wrongPassword'))
                        ]
                    }
                ],
                invoiceTabOnSubmit: [
                    {
                        individualNames: ["decision"],
                        strategies: [
                            this.decisionDefined
                        ]
                    }, {
                        strategies: [
                            this.decisionAllowed,
                            this.atLeastOneInvoiceExists,
                            this.duplicateCheckPerformed
                        ]
                    }, {
                        properties: ["state.complaint.invoiceTab.invoices"],
                        strategies: [
                            forEach([
                                {
                                    properties: ["taxRate"],
                                    individualNames: ["taxRate"],
                                    strategies: [
                                        required(msg('editComplaint.invoiceTab.validation.taxRate'))
                                    ]
                                }, {
                                    properties: ["approved"],
                                    individualNames: ["approved"],
                                    strategies: [
                                        required(msg('editComplaint.invoiceTab.validation.approved'))
                                    ]
                                },
                                {
                                    properties: ["approved"],
                                    individualNames: ["approved", "decision"],
                                    strategies: [
                                        this.invoiceNotApprovedForRejectedDecision(msg('editComplaint.invoiceTab.validation.rejectionNotPossible'))
                                    ]
                                }
                            ])
                        ]
                    }
                ]
            }
        }
        else if(editor.finance && !this.readOnly) {
            let financeValidation = {
                common: commonGroup
            };

            if(documentTabValidationRequired) {
                financeValidation.documentTab = [
                    {
                        properties: ["state.complaint.documentTab.attachments"],
                        strategies: [
                            forEach([
                                {
                                    properties: ["type"],
                                    individualNames: ["documentType"],
                                    strategies: [
                                        required(msg("documentTab.fileTypeError"))
                                    ]
                                }
                            ])
                        ]
                    }
                ];

                financeValidation.documentTabOnSubmit = [
                    {
                        strategies: [
                            this.allAttachmentsViewed
                        ]
                    }
                ];
            }
            return financeValidation;
        }
        else if (editor.admin && documentTabValidationRequired) {
            let adminValidation = {};
            adminValidation.documentTab = [
                {
                    properties: ["state.complaint.documentTab.attachments"],
                    strategies: [
                        forEach([
                            {
                                properties: ["type"],
                                individualNames: ["documentType"],
                                strategies: [
                                    required(msg("documentTab.fileTypeError"))
                                ]
                            }
                        ])
                    ]
                }
            ];
            return adminValidation;
        }
    }


    attachmentsExist = () => {
        let errors = [];
        if (this.state.complaint.documentTab.attachments.length === 0) {
            errors.push(msg('editComplaint.documentTab.validation.missingDocuments'));
        }
        return generateStrategyResult(errors);
    };

    allAttachmentsViewed = () => {
        const state = this.state.complaint.state;
        let errors = [];
        if (state === "IN_PROGRESS") {
            this.state.complaint.documentTab.attachments.some(attachment => {
                if (!attachment.viewers.includes(this.state.complaint.editor.accountName)) {
                    errors = [msg('editComplaint.documentTab.validation.viewedDocumentError')];
                    return true;
                }
                else {
                    return false;
                }
            });
        }
        return generateStrategyResult(errors);
    };

    decisionAllowed = () => {
        const decision = this.state.complaint.invoiceTab.decision;
        const requestedAmountNet = stringToFloat(this.state.complaint.mainTab.requestedAmountNet);
        const approvedAmountNet = stringToFloat(this.state.complaint.invoiceTab.approvedAmountNet);
        let errors = [];

        if (decision === "APPROVED" && requestedAmountNet > approvedAmountNet) {
            errors.push(msg('editComplaint.submitApprovedNotAllowed'));
        } else if (decision === "PARTLY_APPROVED" && (requestedAmountNet === approvedAmountNet || approvedAmountNet === 0)) {
            errors.push(msg('editComplaint.submitPartlyApprovedNotAllowed'));
        }
        return generateStrategyResult(errors);
    };

    decisionDefined = () => {
        let errors = [];
        const decision = this.state.complaint.invoiceTab.decision;
        if (!decision || decision === "" || decision === "OPEN") {
            errors.push(msg('editComplaint.validation.missingDecision'));
        }
        return generateStrategyResult(errors);
    };

    atLeastOneInvoiceExists = () => {
        let errors = [];
        if (this.state.complaint.invoiceTab.invoices.length === 0){
            errors.push(msg('editComplaint.validation.missingInvoices'));
        }
        return generateStrategyResult(errors);
    };

    invoiceNotApprovedForRejectedDecision = (errorMessage) => (invoiceApproved) => {
        let errors;
        if (this.state.complaint.invoiceTab.decision === "REJECTED" && invoiceApproved[0] === "APPROVED") {
            errors = [errorMessage];
        } else {
            errors = [];
        }
        return generateStrategyResult(errors, true);
    };

    duplicateCheckPerformed = () => {
        let errors = [];
        const {duplicateCheckPassed, invoices} = this.state.complaint.invoiceTab;
        if (!duplicateCheckPassed && invoices.length > 0 && this.state.complaint.state === "IN_PROGRESS"){
            errors.push(msg('editComplaint.validation.missingDuplicateCheck'));
        }
        return generateStrategyResult(errors);
    };

    handleTabElementChange = (tabName) => (fieldName) => (changedValue) => {
        const change = {};
        if(changedValue) {
            changedValue = changedValue.target ? changedValue.target.value : changedValue;
        }
        change[fieldName] = changedValue;
        this.handleTabChange(tabName)(change);
    };

    handleTabChange = (tabName) => (changedValues, savedValues) => {
        const updatedFields = {};
        for (let [name, value] of Object.entries(changedValues)) {
            updatedFields[name] = {$set: value};
        }
        const updatedTab = update(this.state.complaint[tabName], updatedFields);
        const complaint = this.state.complaint;
        complaint[tabName] = updatedTab;

        // Used only for delete system files on countersign.
        if (savedValues) {
            this.setState({
                complaint: complaint,
                saved: update(this.state.saved, {
                    $merge: savedValues
                })
            });
        } else {
            this.setState({
                complaint: complaint
            });
        }
    };

    updateInvoices = (invoiceNumber) => {
        const invoices = this.state.complaint.invoiceTab.invoices;
        let invoiceNumbers = invoices.map(i => i.invoiceNumber);
        if(invoiceNumber && !invoiceNumbers.includes(invoiceNumber)) {
            this.request('get', conf.rest.invoiceEndpoint).query({invoiceNumber: invoiceNumber}).send((invoiceResult) => {
                const updatedInvoices = update(invoices, {$push: [invoiceResult.body]});
                this.handleTabChange('invoiceTab')( {'invoices': updatedInvoices});
            })
        }
    };

    getAllowedRoles() {
        return [Role.VPQ_OPERATOR];
    }

    componentDidMount() {
        super.init(() => {
            this.request('get', conf.rest.complaintSecureEndpoint + '/' + this.basket + '/' + this.complaintNumber)
                .query({readOnly: this.requestedReadOnly})
                .send((result) => {
                    this.readOnly = result.body.readOnly;
                    this.countersigningAllowed = result.body.documentTab.editableAreas.indexOf('COUNTERSIGN_PANEL') >= 0;
                    this.renderButtonGroup = result.body.editor.sales ? this.renderSalesButtonGroup : this.renderFinanceButtonGroup;
                    this.validation = this.getValidation(result.body.editor, result.body.documentTab.editableAreas.indexOf('UPLOAD_BUTTON') >= 0,
                        !result.body.documentTab.editableAreas.includes(COUNTERSIGN_AREA));
                    this.setState({
                        complaint: result.body,
                        initialized: true
                    }, this.subscribeOnComplaintUpdates);
                    if (!this.readOnly || this.isAdminEditAllowed()) {
                        this.addCloseConfirmation();
                    }
            });
        });
    }

    subscribeOnComplaintUpdates = () => {
        const tabs = {
            mainTab: !this.state.complaint.mainTab.editable,
            invoiceTab: this.state.complaint.invoiceTab.editableAreas.length === 0,
            imeiTab: !this.state.complaint.imeiTab.editable,
            financeTab: !this.state.complaint.financeTab.editable,
            documentTab: true,
        };
        const url = '/complaint/' + this.basket + '/' + this.complaintNumber + '?' + Object.keys(tabs).map(k => k + '=' + tabs[k]).join('&');
        const socket = new SockJS(url);
        const client = Stomp.over(socket);

        client.debug = null; // disable debug logging

        const updateComplaint = (message) => {
            const newComplaint = JSON.parse(message.body);
            if (this.state.complaint.documentTab.editableAreas.some(a => ['DOCUMENTS_TABLE', 'CREATE_BUTTON', 'UPLOAD_BUTTON'].includes(a))) {
                // user is able to change list of the documents, so we have to update only new sales partner's documents
                const oldDocuments = this.state.complaint.documentTab.attachments;
                const newDocuments = newComplaint.documentTab.attachments;

                newDocuments.forEach(newDocument => {
                    if (newDocument.author === undefined && oldDocuments.every(oldDocument => oldDocument.id !== newDocument.id)) {
                        // the document has been uploaded by Sales Partner via Extend Complaint Form
                        oldDocuments.push(newDocument);
                    }
                });

                newComplaint.documentTab.attachments = oldDocuments;
            }
            // since user always subscribes on document tab update, we have to save his editable areas and token before merge
            newComplaint.documentTab.editableAreas = this.state.complaint.documentTab.editableAreas;
            newComplaint.documentTab.fileToken = this.state.complaint.documentTab.fileToken;
            this.setState({
                complaint: update(this.state.complaint, {$merge: newComplaint})
            });
        };
        client.connect({}, function (frame) {
            client.subscribe('/user/topic' + url, updateComplaint);
        });
    };

    componentWillUnmount() {
        this.removeCloseConfirmation();
    }

    setCloseConfirmationMessage = function(e) {
        e = e || window.event;
        if (e) {
            // In case of IE or Firefox
            e.returnValue = msg('editComplaint.closeWindowConfirmation');
        }
        return msg('editComplaint.closeWindowConfirmation');
    };

    unlockComplaint = () => {
        this.request('delete', conf.rest.lockComplaintEndpoint + '/' + this.complaintNumber).send(()=>{});
    };

    addCloseConfirmation = () => {
        window.addEventListener('beforeunload', this.setCloseConfirmationMessage);
        window.addEventListener('unload', this.unlockComplaint);
    };

    removeCloseConfirmation = () => {
        window.removeEventListener('beforeunload', this.setCloseConfirmationMessage);
        window.removeEventListener('unload', this.unlockComplaint);
    };

    toggleCommentListPopup = () => {
        const commentListPopupOpened = !this.state.commentListPopupOpened;
        this.setState({
            commentListPopupOpened: commentListPopupOpened
        });
        if (!commentListPopupOpened) {
            this.commentList.focus();
        }
    };

    openBasketHistory = () => {
        window.open('/businessLog/' + btoa(this.complaintNumber), '_blank',
            'left=100,top=100,width=570,height=800,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes');
    };

    showErrorMessages = (validationResult) => {
        this.setState({
            errorMessages: validationResult.getErrorMessages(),
            processing: false
        }, () => {
            const errorBox = document.getElementById("errorBox");
            if (errorBox) {
                errorBox.focus()
            }
        });
    };

    saveComplaint = (fullValidationRequired) => {
        if (fullValidationRequired) {
            const validationResult = validate(this, this.validation, ["common", "mainTab", "documentTab"]);
            if (validationResult.isEmpty()) {
                this.editComplaint(SAVE_COMPLAINT);
            } else {
                this.showErrorMessages(validationResult);
            }
        } else {
            //only document validation required in case of admin save in waiting for archive basket
            const validationResult = validate(this, this.validation, ["documentTab"]);
            if (validationResult.isEmpty()) {
                this.editComplaint(CLOSE_COMPLAINT);
            } else {
                this.showErrorMessages(validationResult);
            }
        }
    };

    submitComplaint = () => {
        const operation = this.state.complaint.finalBasket ? CLOSE_COMPLAINT : SUBMIT_COMPLAINT;
        const validationResult = validate(this, this.validation, ["common", "mainTab", "invoiceTabOnSubmit", "documentTab", "documentTabOnSubmit"]);
        if (!validationResult.isEmpty() || this.checkForOverpayment(() => this.editComplaint(operation))) {
            this.showErrorMessages(validationResult);
        }
    };

    closeComplaint = () => {
        const validationResult = validate(this, this.validation, ["common", "mainTab", "invoiceTabOnSubmit", "documentTab", "documentTabOnSubmit"]);
        if (!validationResult.isEmpty() || this.checkForOverpayment(() => this.editComplaint(CLOSE_COMPLAINT))) {
            this.showErrorMessages(validationResult);
        }
    };

    /**
     * If the complaint is APPROVED and approved amount is overpaid (more than requested), asks user for confirmation and run callback on positive answer.
     * If complaint decision is different or approved amount is not overpaid, runs callback without confirmation dialog.
     * @param noOverpaymentCallback callback function to run if no overpayment is found.
     * @returns {boolean} is overpayment found?
     */
    checkForOverpayment(noOverpaymentCallback){
        // User answer in confirmation dialog. True by default for the case, when confirmation dialog shouldn't be shown.
        let confirmation = true;
        if (this.state.complaint.invoiceTab.decision === "APPROVED") {
            const requestedAmountNet = parseInt(this.state.complaint.mainTab.requestedAmountNet, 10);
            const approvedAmountNet = parseInt(this.state.complaint.invoiceTab.approvedAmountNet, 10);
            if (requestedAmountNet < approvedAmountNet) {
                confirmation = confirm(msg('editComplaint.submitConfirmation'));
            }
        }
        if (confirmation) {
            noOverpaymentCallback();
        }
        return !confirmation;
    }

    editComplaint = (complaintOperation) => {
        this.setState({
            processing: true,
            errorMessages: {}
        });
        const complaint = this.state.complaint;
        const newComments = update(this.state.newComments, {$push: [this.state.userComment]});
        const editComplaint = update(complaint, {newComments: {$set: newComments}});

        if (complaint.mainTab.uvo && !complaint.mainTab.uvo.vo) {
            editComplaint.mainTab.uvo = undefined;
        }

        this.request('post', COMPLAINT_ENDPOINTS.get(complaintOperation)).params(editComplaint).send(() => {
            this.removeCloseConfirmation();
            this.closeWindow();
        }, (error, result) => {
            const validationResult = new ValidationResult(this.state.errorMessages);
            let errorText;

            if (result && result.statusCode === 423) {
                const fileWithError = result.body.message;
                errorText = replacePlaceholders(msg('editComplaint.save.openFileError'), fileWithError);
            } else {
                errorText = msg('common.technicalError');
            }

            validationResult.setError("manualSave", errorText);
            this.showErrorMessages(validationResult);
        })
    };

    resubmitComplaint = () => {
        const validationResult = validate(this, this.validation, ["common", "mainTab", "documentTab"]);
        if (validationResult.isEmpty()) {
            this.editComplaint(RESUBMIT_COMPLAINT);
        } else {
            this.showErrorMessages(validationResult);
        }
    };

    countersignComplaint = () => {
        let validationResult = validate(this, this.validation, ["documentTabOnCountersign"]);
        if (validationResult.isEmpty()) {
            this.setState({
                processing: true,
                errorMessages: {}
            });
            const params = {
                complaintNumber: this.complaintNumber,
                countersigningResult: this.state.countersigningResult,
                password: this.state.password,
                comments: update(this.state.newComments, {$push: [this.state.userComment]}),
                fileToken: this.state.complaint.documentTab.fileToken,
                deletedAttachments: this.state.saved['deletedAttachments']
            };

            this.request('post', conf.rest.complaintCountersignEndpoint).params(params).send(() => {
                this.removeCloseConfirmation();
                this.closeWindow();
            }, (error, result) => {
                let errorText;
                let invalidComponent;
                if (result && result.statusCode === 422) {
                    errorText = msg('editComplaint.documentTab.validation.wrongPassword');
                    invalidComponent = ["password"];
                } else if (result && result.status === 403) {
                    errorText = msg('editComplaint.documentTab.validation.intrusionLocked');
                    invalidComponent = [];
                } else {
                    errorText = msg("common.technicalError");
                    invalidComponent = [];
                }
                validationResult = new ValidationResult(this.state.errorMessages);
                validationResult.setGroup("documentTabOnCountersign", {[invalidComponent]: [errorText]});
                this.showErrorMessages(validationResult);
            });
        } else {
            this.showErrorMessages(validationResult);
        }
    };

    closeWindowWithoutConfirmation = () => {
        this.removeCloseConfirmation();
        this.closeWindow();
    };

    renderSalesButtonGroup = () => {
        const submitButtonsDisabled = this.readOnly || this.countersigningAllowed;
        const resubmitSalesButtonRendered = this.state.complaint.resubmitSalesButtonRendered;
        const rejectButtonRendered = this.state.complaint.finalSalesBasket;
        return (
            <div className={theme.btnGroupMini}>
                <button onClick={this.submitComplaint} disabled={submitButtonsDisabled}>
                    {msg('editComplaint.submitButton')}
                </button>
                <button onClick={() => this.saveComplaint(!this.isAdminEditAllowed())} disabled={this.readOnly && !this.isAdminEditAllowed()}>
                    {msg('editComplaint.saveButton')}
                </button>
                {resubmitSalesButtonRendered ?
                    <button onClick={this.resubmitComplaint} disabled={submitButtonsDisabled}>
                        {msg('editComplaint.resubmitButton')}
                    </button> : ""
                }
                {rejectButtonRendered ?
                    <button onClick={this.resubmitComplaint} disabled={submitButtonsDisabled}>
                        {msg('editComplaint.rejectButton')}
                    </button> : ""
                }
                {this.readOnly ? <button onClick={this.closeWindowWithoutConfirmation}>{msg('editComplaint.exitButton')}</button> : ""}
                {this.renderReportButton()}
            </div>
        );
    };

    renderFinanceButtonGroup = () => {
        const resubmitFinanceButtonRendered = this.state.complaint.resubmitFinanceButtonRendered;
        return (
            <div className={theme.btnGroupMini}>
                <button onClick={this.submitComplaint} disabled={this.readOnly}>
                    {msg('editComplaint.submitButton')}
                </button>
                <button onClick={() => this.saveComplaint(!this.isAdminEditAllowed())} disabled={this.readOnly && !this.isAdminEditAllowed()}>
                    {msg('editComplaint.saveButton')}
                </button>
                {this.renderFinanceInputBasketButtons()}
                {resubmitFinanceButtonRendered ?
                    <button onClick={this.resubmitComplaint} title={msg("editComplaint.reopenButtonText")} disabled={this.readOnly}>
                        {msg('editComplaint.reopenButton')}
                    </button> : ""
                }
                {this.readOnly ? <button onClick={this.closeWindowWithoutConfirmation}>{msg('editComplaint.exitButton')}</button> : ""}
                {this.renderReportButton()}
            </div>
        );
    };

    renderFinanceInputBasketButtons() {
        if (this.state.complaint.financeInputBasket) {
            return (
                <span>
                    <button onClick={this.closeComplaint}
                        disabled={this.readOnly || this.state.complaint.closeComplaintButtonDisabled }>
                        {msg('editComplaint.closeButton')}
                    </button>
                    <button onClick={this.resubmitComplaint} disabled={this.readOnly}>{msg('editComplaint.toCountersigningButton')}</button>
                </span>
            )
        }
    };

    renderReportButton() {
        return (
            <div>
                <button onClick={this.openBasketHistory}>
                    {msg('editComplaint.reportButton')}
                </button>
            </div>
        )

    }

    addNewComments = (newComment) => {
        this.setState({
            newComments: update(this.state.newComments, {$push: [newComment]})
        })
    };

    addEmailComments = (newEmailComment) => {
        const comments = update(this.state.complaint.comments, {$unshift: [newEmailComment]});
        this.setState({
            complaint: update(this.state.complaint, {comments: {$set: comments}})
        })
    };

    /**
     * Downloads an attachment with the specified index in attachment array.
     * If current user is not in the attachment viewer list, adds him.
     */
    downloadAttachment = (index) => {
        const downloadedRow = this.state.complaint.documentTab.attachments[index];
        const fileName = downloadedRow.name;
        const downloadUrl = conf.rest.attachmentEndpoint + '/' + this.complaintNumber + '/' + this.state.complaint.documentTab.fileToken + '/' + encodeURIComponent(fileName);
        this.request('get', downloadUrl).send((result) => {
            const attachment = result.body;
            const byteArray = new Uint8Array(this.strToByteArray(attachment.file));
            const file = new Blob([byteArray]);
            const displayableName = attachment.displayableName || downloadedRow.displayableName;
            fileDownload(file, displayableName);

            let currentAttachments = this.state.complaint.documentTab.attachments;
            let viewers = currentAttachments[index].viewers;
            let editor = this.state.complaint.editor.accountName;
            if (!viewers.includes(editor)) {
                let updatedAttachments = update(currentAttachments, {[index]: {viewers: {$push: [editor]}}});
                this.handleTabElementChange('documentTab')('attachments')(updatedAttachments);
            }
        });
    };

    showProgressBar = () => {
        this.setState({processing: true});
    };

    hideProgressBar = () => {
        this.setState({processing: false});
    };

    renderCountersigningPanel = () => {
        return (
            <CountersigningPanel countersigningResult={this.state.countersigningResult} password={this.state.password}
                complaintNumber={this.complaintNumber} basket={this.basket}
                countersigningHistoryItems={this.state.complaint.documentTab.countersigningHistoryItems}
                countersigningAllowed={this.countersigningAllowed}
                errorMessages={this.state.errorMessages} onChange={this.handleChange}
                onCountersign={this.countersignComplaint}/>
        );
    };

    renderLockedWarningMessage = () => {
        const lockedBy = this.state.complaint.lockedBy;
        if (lockedBy && this.state.complaint.editor.accountName !== lockedBy) {
            return (
                <div className={theme.msgLock}>
                    {replaceJsxPlaceholders(msg('editComplaint.warning.lockedComplaint'), <strong>{lockedBy}</strong>)}
                </div>
            );
        }
    };

    hideInfoIcon = () => {
        this.setState({
            complaint: Object.assign({}, this.state.complaint, {notProcessed: false})
        });
    };

    isAdminEditAllowed = () => {
        return this.state.complaint.adminEditAllowed;
    };

    getDialog() {
        const lockedWarningMessage = this.renderLockedWarningMessage();
        return (
            <div className={theme.client}>
                <Header value={msg('vprekla.header')}>
                    <ul>
                        <li>
                            <a href="#">{msg('navigationMenu.helpLnk')}</a>
                        </li>
                        <li>
                            <LogoutTimer timeout={this.state.logoutTimeout} onTimeOver={() => this.logout(this.props.tvppLogoutUrl)}/>
                        </li>
                    </ul>
                </Header>
                <section className={theme.editComplaint}>
                    <h2>{msg('editComplaint.header')}</h2>
                    <div className={theme.content}>
                        <ul className={theme.info}>
                            <li>
                                <span>{msg('editComplaint.complaintNumber')}</span> <span>{this.complaintNumber}</span>
                            </li>
                            <li>
                                <span>{msg('editComplaint.creationDate')}</span> <span>{moment(this.state.complaint.created).format('DD.MM.YYYY')}</span>
                            </li>
                            <li>
                                <Input id="email-txt" value={this.state.complaint.email} name="email" errorMessages={this.state.errorMessages}
                                    onChange={this.handleNestedChange('complaint', 'email')} required label={msg('editComplaint.emailLabel')}
                                    className={theme.row} disabled={this.readOnly || this.countersigningAllowed}/>
                            </li>
                        </ul>

                        <ErrorBox id="errorBox" groups={["common", "manualSave", "mainTab", "hvoReload", "uvoReload", "invoiceTab", "invoiceTabOnSubmit",
                            "imeiTab", "documentTab", "documentTabOnSubmit", "documentAddition", "documentTabOnCountersign", "sendAttachment"]}
                            errorMessages={this.state.errorMessages}/>

                        <TabView className={theme.tabWrap} errorMessages={this.state.errorMessages} hideInfoIcon={this.hideInfoIcon}
                            infoMessage={this.state.complaint.readOnly ? msg('editComplaint.infoMessage.readOnly') : msg('editComplaint.infoMessage')}>
                            <TabPanel header={msg('editComplaint.mainTab.header')} groups={["mainTab", "hvoReload", "uvoReload"]}
                                newDataUploaded={this.state.complaint.notProcessed}>
                                {lockedWarningMessage}
                                <MainTab value={this.state.complaint.mainTab} complaintTypes={this.state.complaintTypes}
                                    errorMessages={this.state.errorMessages} onError={this.handleChange('errorMessages')}
                                    approvedAmountNet={this.state.complaint.invoiceTab.approvedAmountNet}
                                    approvedAmountTotal={this.state.complaint.invoiceTab.approvedAmountTotal}
                                    onChange={this.handleTabChange('mainTab')}
                                    onElementChange={this.handleTabElementChange('mainTab')}
                                    onStart={this.showProgressBar} onComplete={this.hideProgressBar}
                                    removeCloseConfirmation={this.removeCloseConfirmation} readOnly={!this.state.complaint.mainTab.editable}>
                                </MainTab>
                            </TabPanel>
                            <TabPanel header={msg('editComplaint.invoiceTab.header')} groups={["invoiceTab", "invoiceTabOnSubmit"]}
                                newDataUploaded={this.state.complaint.notProcessed}>
                                {lockedWarningMessage}
                                <InvoiceTab value={this.state.complaint.invoiceTab}
                                    errorMessages={this.state.errorMessages} onChange={this.handleTabChange('invoiceTab')}
                                    onDelete={this.handleChange} onError={this.handleChange('errorMessages')}
                                    onDuplicateSearchStart={this.showProgressBar} onDuplicateSearchStop={this.hideProgressBar}
                                    complaintNumber={this.complaintNumber} editableAreas={this.state.complaint.invoiceTab.editableAreas}>
                                </InvoiceTab>
                            </TabPanel>
                            <TabPanel header={msg('editComplaint.imeiTab.header')} groups={["imeiTab"]}
                                newDataUploaded={this.state.complaint.notProcessed}>
                                {lockedWarningMessage}
                                <ImeiTab value={this.state.complaint.imeiTab} errorMessages={this.state.errorMessages}
                                    onChange={this.handleTabChange('imeiTab')}
                                    onImeiReload={this.updateInvoices}
                                    onError={this.handleChange('errorMessages')}
                                    readOnly={!this.state.complaint.imeiTab.editable}>
                                </ImeiTab>
                            </TabPanel>
                            <TabPanel header={msg("editComplaint.financeTab.header")} newDataUploaded={this.state.complaint.notProcessed}>
                                {lockedWarningMessage}
                                <FinanceTab value={this.state.complaint.financeTab} onChange={this.handleTabElementChange('financeTab')}
                                    readOnly={!this.state.complaint.financeTab.editable}/>
                            </TabPanel>
                            <TabPanel header={msg('editComplaint.documentTab.header')} newDataUploaded={this.state.complaint.notProcessed}
                                groups={["documentTab", "documentTabOnSubmit", "documentAddition", "documentTabOnCountersign", "sendAttachment"]}>
                                {lockedWarningMessage}
                                <DocumentTab value={this.state.complaint.documentTab} errorMessages={this.state.errorMessages}
                                    onChange={this.handleTabChange('documentTab')} onDownload={this.downloadAttachment}
                                    onError={this.handleChange('errorMessages')} onProgressStart={this.showProgressBar} onProgressComplete={this.hideProgressBar}
                                    addCloseConfirmation={this.addCloseConfirmation} removeCloseConfirmation={this.removeCloseConfirmation}
                                    complaint={this.state.complaint} editor={this.state.complaint.editor.accountName}
                                    editableAreas={this.state.complaint.documentTab.editableAreas} onRemove={this.addNewComments} onEmailSend={this.addEmailComments}
                                    countersigningPanel={this.renderCountersigningPanel} adminEditable={this.isAdminEditAllowed()}
                                    uvoAvailable={this.state.complaint.mainTab.uvo !== undefined}>
                                </DocumentTab>
                            </TabPanel>
                        </TabView>

                        <div className={theme.blcGroup + ' ' + theme.clear}>
                            <div className={theme.blcRemarkNew}>
                                <TextArea id="userComment-textarea" value={this.state.userComment} name="userComment" errorMessages={this.state.errorMessages}
                                    onChange={this.handleChange('userComment')}
                                    required={!this.state.complaint.documentTab.editableAreas.includes(COUNTERSIGN_AREA) && !this.isAdminEditAllowed()}
                                    label={msg('editComplaint.commentLabel')} disabled={this.readOnly && !this.isAdminEditAllowed()}/>
                            </div>
                            <CommentList value={this.state.complaint.comments} header={msg('editComplaint.commentList.header')}
                                onClick={() => this.toggleCommentListPopup} icon={loupePlusIcon}
                                ref={(commentList) => this.commentList = commentList}
                                hint={msg('editComplaint.commentList.showPopup')} className={theme.blcRemark}/>
                            <AttachmentList value={this.state.complaint.documentTab.attachments} header={msg("editComplaint.attachmentList.header")}
                                onDownload={this.downloadAttachment}/>
                        </div>
                        {this.renderButtonGroup()}
                    </div>
                </section>
                <Popup visible={this.state.commentListPopupOpened}>
                    <CommentList value={this.state.complaint.comments} header={msg('editComplaint.commentList.header')}
                        onClick={() => this.toggleCommentListPopup} icon={loupeMinusIcon}
                        ref={(commentListPopup) => commentListPopup ? commentListPopup.focus() : {}}
                        hint={msg('editComplaint.commentList.hidePopup')} className={theme.commentHistoryPopupContent}/>
                </Popup>
                <ProgressBar hidden={!this.state.processing} />
            </div>
        );
    }
};



// WEBPACK FOOTER //
// ./src/dialogs/VPRekla/EditComplaint/index.js